import React from "react";
import PropTypes from "prop-types";
import SessionStore from "../../services/session/SessionStore";
import UUID from "uuid/v4";
import * as remoteLog from "../../common/services/remoteLog";
import config from "../../config"

/**
 * provides a fallback UI in case the application crashes hard
 */
export default class UnrecoveredErrorHandler extends React.Component {
  static propTypes = {
    /** child elements to render if there's no error */
    children: PropTypes.any.isRequired,
    /** function to be called on error, Passed the error object and a serializable javascript object with relevant info */
    reportError: PropTypes.func.isRequired,
  };

  static defaultProps = {
    reportError: (...args) => {
      logErrorLocally(...args);
      reportErrorToServer(...args);
      reportLocalStorageToServer(...args);
    },
  };

  state = {
    hasError: false,
    error: undefined
  };

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, errorId: UUID(), error };
  }

  componentDidCatch(error, errorInfo) {
    this.props.reportError(error, {
      ...errorInfo,
      username: SessionStore.user && SessionStore.user.username,
      errorId: this.state.errorId,
    });
  }

  tryRecoverFatal() {
    try {
      localStorage.clear();
    } catch (ex) {}
    try {
      sessionStorage.clear();
    } catch (ex) {}
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="alert alert-primary m-5">
          <h4>Oops!</h4>
          <p>
            A critical application error occurred. You can try reloading. If the
            problem persists, please send us a message referencing the below
            error ID.
          </p>
          <pre>
            <code>{this.state.errorId}</code>
            { config.isDevelopment && <code>{renderError(this.state.error)}</code> }
          </pre>
          <div className="d-flex flex-row-reverse">
            <button
              className="btn btn-secondary"
              onClick={this.tryRecoverFatal}
            >
              Reload
            </button>
          </div>
        </div>
      );
    } else {
      return this.props.children;
    }
  }
}
function renderError(error) {
  return error.toString() + "\n" + error?.stack
}
export function reportErrorToServer(error, errorInfo) {
  remoteLog.error(
    `Error id ${errorInfo.errorId}. Application crashed from error ${
      error && error.name
    }: ${error && error.toString()}`,
    errorInfo
  );
}

export function logErrorLocally(error, errorInfo) {
  console.error("Application crashed " + errorInfo.errorId, error);
}

export function reportLocalStorageToServer(error, errorInfo) {
  const keySizes = Object.entries(localStorage)
    .map(([k, v]) => [k, v.length])
    .sort((a, b) => b[1] - a[1]);
  const totalKeys = keySizes.length;
  const totalLength = keySizes.reduce(
    (sum, [k, count]) => sum + k.length + count,
    0
  );
  const keyLength = keySizes.reduce((sum, [k, count]) => sum + k.length, 0);
  const valueLength = keySizes.reduce((sum, [k, count]) => sum + count, 0);
  const topKeys = keySizes
    .slice(0, 10)
    .map((x) => `${x[0]}: ${x[1]}`)
    .join("\n");
  remoteLog.error(`Error id ${errorInfo.errorId} local storage analysis`, {
    errorId: errorInfo.errorId,
    totalKeys,
    totalLength,
    keyLength,
    valueLength,
    topKeys,
  });
}
